// MA-496 (Journey v3, PR-A / A10 + A12) — assembles the finite stone-tap
// SPEAK-text domain from journeyView.ts's two closed Records
// (KID_LABEL_MAP: 54 kid labels, KID_STATUS_LABEL: 7 status phrases) via
// narrationUiStrings.ts's `stoneTapSpeakText`. Kept in its own module
// (rather than inside narrationUiStrings.ts, which is deliberately
// import-free — see that file's header) so narrationUiStrings.ts's
// zero-import discipline survives this addition.
import { stoneTapSpeakText } from "@/engine/narrationUiStrings";
import { KID_LABEL_MAP, KID_STATUS_LABEL } from "./journeyView";
import type { Status } from "./skillTreeStatus";

/** Every one of the 54*7 = 378 finite (kidLabel, distinct statusPhrase) variants —
 *  ⭐MA-500: this is now the exact domain scripts/content/narratableStrings.mjs's
 *  enumerator expands (imported directly, see that file), folding it into
 *  the narration-coverage practice denominator. Exported so the T-D sweep /
 *  a unit bite can assert every LIVE stone's actual speak() call resolves
 *  to a member of this array (never an ad hoc inline template) — see
 *  scripts/content/__tests__/narratableStrings.test.mjs's MA-500 parity
 *  bite, which imports this SAME array (not a re-typed copy) to prove the
 *  enumerator's source and the runtime's source are byte-identical by
 *  construction. */
//
// MA-85: built over the DISTINCT status phrases. `not_yet` reuses the
// recorded "Earlier step" (KID_STATUS_LABEL), so 8 statuses map to the same 7
// phrases and the domain stays the same 54*7 = 378 recorded strings — no new
// audio, and no duplicate entries.
export const JOURNEY_STONE_SPEAK_PHRASES: readonly string[] = [...new Set(Object.values(KID_STATUS_LABEL))];
export const JOURNEY_STONE_SPEAK_STRINGS: readonly string[] = Object.values(KID_LABEL_MAP).flatMap((kidLabel) =>
  JOURNEY_STONE_SPEAK_PHRASES.map((statusPhrase) => stoneTapSpeakText(kidLabel, statusPhrase)),
);

/** The single call site JourneyPath.tsx's tap handler uses — guarantees
 *  the spoken string is always a member of JOURNEY_STONE_SPEAK_STRINGS by
 *  construction (same two closed Records, same function). */
export function speakTextForStone(kidLabel: string, status: Status): string {
  return stoneTapSpeakText(kidLabel, KID_STATUS_LABEL[status]);
}
